import React, { useRef, useCallback, useState, useMemo } from 'react';
import { find, isEmpty, noop } from 'lodash';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import ReactIcon from '../../../Icons';
import useOnClickOutside from '../../../hooks/useOnClickOutside';

export default function DropdownSelect({
  label,
  options,
  disabled,
  onSelect = noop,
}) {
  const [currentScrollPosition, setScrollPosition] = useState(0);
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [searchKey, setSearchKey] = useState('');
  const onToggleDropdown = useCallback(() => {
    setDropdownOpen(!isDropdownOpen);
    setSearchKey('');
    setScrollPosition(window.scrollY);
  }, [isDropdownOpen, setDropdownOpen]);

  const onSelectCallback = useCallback(
    (option) => {
      setDropdownOpen(false);
      onSelect(option);
      setSearchKey('');
      window.scroll(0, currentScrollPosition);
    },
    [onSelect, currentScrollPosition],
  );

  const onSearch = useCallback(
    (e) => {
      const { value } = e.target;
      setSearchKey(value);
    },
    [setSearchKey],
  );
  const selectedValue = find(options, 'isSelected');

  const onClickOutside = useCallback(() => {
    if (isDropdownOpen) {
      setDropdownOpen(false);
      setSearchKey('');
    }
  }, [isDropdownOpen]);
  const wrapper = useRef();
  useOnClickOutside(wrapper, onClickOutside);

  const filteredOptions = useMemo(
    () =>
      options.filter((option) =>
        option.label.toLowerCase().includes(searchKey.toLowerCase()),
      ),
    [searchKey, options],
  );
  return (
    <div
      ref={wrapper}
      className={classNames('dropdown-select', {
        disabled,
        open: isDropdownOpen,
      })}
    >
      <header className="dropdown-select-header">{label}</header>
      <div className="dropdown-select-container">
        <button
          type="button"
          onClick={onToggleDropdown}
          className="dropdown-select-toggle"
          disabled={disabled}
          data-testid="dropdown-select-button"
        >
          {selectedValue && (
            <span className="font-bold">{selectedValue.label}</span>
          )}
          {!selectedValue && <span>{label}</span>}
          <span className="dropdown-select-icon">
            <ReactIcon type="keyboard_arrow_right" size={24} />
          </span>
        </button>
        <div className="dropdown-select-list-container">
          <div className="dropdown-select-search-container">
            <div className="dropdown-select-search-input">
              <input
                type="text"
                onChange={onSearch}
                value={searchKey}
                aria-label="Search field"
              />
              <ReactIcon type="search" size={17} />
            </div>
          </div>
          <ul className="dropdown-select-list">
            {isEmpty(filteredOptions) && !isEmpty(searchKey) && (
              <div className="no-results-container">
                <ReactIcon type="no-results" size={58} />
              </div>
            )}
            {filteredOptions.map((option) => (
              <li
                className={classNames('dropdown-select-list-item', {
                  selected: option.isSelected,
                })}
                key={option.value}
              >
                <button type="button" onClick={() => onSelectCallback(option)}>
                  {option.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

DropdownSelect.propTypes = {
  label: PropTypes.string,
  options: PropTypes.array,
  disabled: PropTypes.bool,
  onSelect: PropTypes.func,
};
